import type { FC, KeyboardEvent } from 'react'
import type { ConfigState } from '@/lib/configurator'

import type {
    BatteryKey,
    Configuration,
    ForkliftModel,
    OptionKey,
    PaintKey,
    TireKey
} from '@/types'

import { useEffect, useId, useRef, useState } from 'react'
import { ArrowRight, Check, RotateCcw, RotateCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cmsTag } from '@/lib/cms'
import { useStore } from '@/lib/store'
import { sceneInput } from '@/lib/scene-state'
import { formatNumber, formatUsd } from '@/lib/format'
import { useAnimatedNumber } from '@/hooks/use-animated-number'
import { DragStage, nudgeTruck } from '@/components/landing/drag-stage'
import { configureIntro } from '@/constants/content'

import {
    buildSummary,
    configStore,
    leaseFor,
    quoteStore,
    runtimeFor,
    selectedModel,
    totalFor,
    truckFor
} from '@/lib/configurator'

import {
    batteries,
    options,
    paints,
    tires
} from '@/constants/lineup'

type Tab = 'model' | 'color' | 'power' | 'options'

const tabs: { key: Tab, label: string }[] = [
    { key: 'model', label: "Model" },
    { key: 'color', label: "Color" },
    { key: 'power', label: "Power" },
    { key: 'options', label: "Options" }
]

type PulseKey = NonNullable<ConfigState['pulse']>['key']

const cardClass = (active: boolean) => cn(
    'relative flex cursor-pointer rounded-2xl border transition-[border-color,background-color,box-shadow] duration-200 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand',
    active
        ? 'border-brand/80 bg-brand/[0.07] shadow-[0_0_0_1px_var(--brand)_inset]'
        : 'border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04]'
)

const priceLabel = (price: number) => (price ? `+${formatUsd(price)}` : "Included")

interface ConfiguratorProps {
    models: ForkliftModel[]
}

export const Configurator: FC<ConfiguratorProps> = ({ models }) => {
    const id = useId()
    const config = useStore(configStore, state => state)
    const model = selectedModel(models, config.modelSlug)
    const [tab, setTab] = useState<Tab>('model')
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

    useEffect(() => {
        const current = configStore.get()
        const initial = selectedModel(models, current.modelSlug)

        if (!initial) return

        configStore.set({ modelSlug: initial.slug, truck: truckFor(initial, current.paint) })
    }, [models])

    const total = model ? totalFor(model, config) : 0
    const animatedTotal = useAnimatedNumber(total)

    if (!model) return null

    const update = (patch: Partial<Configuration>, pulse?: PulseKey) => {
        configStore.set(state => {
            const next = { ...state, ...patch }

            return {
                ...patch,
                truck: truckFor(selectedModel(models, next.modelSlug), next.paint),
                pulse: pulse ? { key: pulse, at: performance.now() } : state.pulse
            }
        })
    }

    const toggleOption = (key: OptionKey) => {
        const enabled = config.options.includes(key)

        update(
            { options: enabled ? config.options.filter(option => option !== key) : [...config.options, key] },
            enabled ? undefined : key
        )
    }

    const onTabKey = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
        if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return

        event.preventDefault()

        const next = (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length

        setTab(tabs[next].key)
        tabRefs.current[next]?.focus()
    }

    const request = () => {
        const build = buildSummary(model, config)

        quoteStore.set(state => ({ build, version: state.version + 1 }))
        document.getElementById('quote')?.scrollIntoView({ behavior: sceneInput.reducedMotion ? 'auto' : 'smooth', block: 'start' })
    }

    const runtime = runtimeFor(model, config.battery)

    const specs = [
        { label: "Capacity", value: formatNumber(model.capacityKg), unit: 'kg' },
        { label: "Lift height", value: formatNumber(model.liftHeightM, 1), unit: 'm' },
        { label: "Runtime", value: formatNumber(runtime), unit: 'h' },
        { label: "Top speed", value: formatNumber(model.topSpeedKmh), unit: 'km/h' }
    ]

    return (
        <section
            id='configure'
            data-chapter='5'
            data-hold='0.32'
            aria-labelledby='configure-title'
            className='relative min-h-[100svh]'
        >
            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-0 bg-linear-to-t from-background via-background/85 via-45% to-transparent to-[62%] [opacity:var(--fade,1)] landscape:bg-linear-to-l landscape:from-background/90 landscape:via-background/40 landscape:via-30% landscape:to-transparent landscape:to-55%'
            />

            <DragStage className='absolute inset-x-0 top-16 h-[36svh] landscape:inset-y-0 landscape:left-0 landscape:top-0 landscape:h-full landscape:w-[60%]' />

            <div className='pointer-events-none relative mx-auto flex min-h-[100svh] w-full max-w-7xl flex-col justify-end gap-6 px-5 pb-10 pt-[42svh] sm:px-8 landscape:flex-row landscape:items-center landscape:justify-between landscape:py-24'>
                <div className='chapter-content pointer-events-auto hidden max-w-sm self-end pb-2 landscape:block'>
                    <p className='font-heading font-expanded text-3xl font-extrabold text-white'>
                        <span data-mdlfy-cms={cmsTag('forklift_models', model.id, 'name')}>{model.name}</span>
                        {' '}
                        <span className='text-brand' data-mdlfy-cms={cmsTag('forklift_models', model.id, 'edition')}>{model.edition}</span>
                    </p>
                    <p className='mt-2 text-base font-medium text-white/80' data-mdlfy-cms={cmsTag('forklift_models', model.id, 'tagline')}>
                        {model.tagline}
                    </p>
                    <p className='mt-3 text-sm leading-relaxed text-white/60' data-mdlfy-cms={cmsTag('forklift_models', model.id, 'description')}>
                        {model.description}
                    </p>

                    <div className='mt-6 flex items-center gap-2'>
                        <button
                            type='button'
                            onClick={() => nudgeTruck(-1)}
                            aria-label="Rotate the truck left"
                            className='inline-flex size-10 items-center justify-center rounded-full border border-white/15 bg-black/30 text-white transition-colors hover:border-brand hover:text-brand focus-visible:outline-2 focus-visible:outline-brand'
                        >
                            <RotateCcw className='size-4' />
                        </button>
                        <button
                            type='button'
                            onClick={() => nudgeTruck(1)}
                            aria-label="Rotate the truck right"
                            className='inline-flex size-10 items-center justify-center rounded-full border border-white/15 bg-black/30 text-white transition-colors hover:border-brand hover:text-brand focus-visible:outline-2 focus-visible:outline-brand'
                        >
                            <RotateCw className='size-4' />
                        </button>
                        <span className='ml-2 text-xs text-white/50'>
                            Drag to look around
                        </span>
                    </div>
                </div>

                <div className='chapter-content pointer-events-auto w-full landscape:w-[25rem] xl:w-[27rem]'>
                    <div className='overflow-hidden rounded-3xl border border-white/10 bg-card/95 shadow-[0_40px_120px_-40px_rgba(0,0,0,0.9)]'>
                        <div className='flex items-start justify-between gap-4 px-5 pt-5 sm:px-6 sm:pt-6'>
                            <div>
                                <p className='flex items-center gap-3 font-mono text-xs font-medium uppercase tracking-[0.22em]'>
                                    <span className='text-brand'>
                                        {configureIntro.number}
                                    </span>
                                    <span aria-hidden='true' className='h-px w-8 bg-brand/50' />
                                    <span className='text-white/70'>
                                        {configureIntro.eyebrow}
                                    </span>
                                </p>
                                <h2 id='configure-title' className='mt-3 font-heading font-expanded text-2xl font-extrabold tracking-tight text-white sm:text-[1.7rem]'>
                                    {configureIntro.title}
                                </h2>
                            </div>

                            <div className='flex gap-1.5 landscape:hidden'>
                                <button
                                    type='button'
                                    onClick={() => nudgeTruck(-1)}
                                    aria-label="Rotate the truck left"
                                    className='inline-flex size-10 items-center justify-center rounded-full border border-white/15 text-white focus-visible:outline-2 focus-visible:outline-brand'
                                >
                                    <RotateCcw className='size-4' />
                                </button>
                                <button
                                    type='button'
                                    onClick={() => nudgeTruck(1)}
                                    aria-label="Rotate the truck right"
                                    className='inline-flex size-10 items-center justify-center rounded-full border border-white/15 text-white focus-visible:outline-2 focus-visible:outline-brand'
                                >
                                    <RotateCw className='size-4' />
                                </button>
                            </div>
                        </div>

                        <div role='tablist' aria-label="Configuration steps" className='mt-4 flex gap-1 px-4 sm:px-5'>
                            {tabs.map((item, index) => (
                                <button
                                    key={item.key}
                                    ref={element => {
                                        tabRefs.current[index] = element
                                    }}
                                    type='button'
                                    role='tab'
                                    id={`${id}-tab-${item.key}`}
                                    aria-selected={tab === item.key}
                                    aria-controls={`${id}-panel-${item.key}`}
                                    tabIndex={tab === item.key ? 0 : -1}
                                    onClick={() => setTab(item.key)}
                                    onKeyDown={event => onTabKey(event, index)}
                                    className={cn(
                                        'flex-1 rounded-full px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-brand',
                                        tab === item.key ? 'bg-white text-background' : 'text-white/60 hover:bg-white/[0.06] hover:text-white'
                                    )}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>

                        <div
                            role='tabpanel'
                            id={`${id}-panel-${tab}`}
                            aria-labelledby={`${id}-tab-${tab}`}
                            className='mt-4 max-h-[30svh] overflow-y-auto overscroll-contain px-4 pb-4 sm:px-5 landscape:max-h-[min(40svh,22rem)] short:landscape:max-h-[34svh]'
                        >
                            {tab === 'model' && (
                                <fieldset>
                                    <legend className='sr-only'>Choose a model</legend>
                                    <div className='grid gap-2.5'>
                                        {models.map(item => {
                                            const active = item.slug === model.slug

                                            return (
                                                <label key={item.slug} className={cn(cardClass(active), 'items-center gap-4 p-4')}>
                                                    <input
                                                        type='radio'
                                                        name={`${id}-model`}
                                                        value={item.slug}
                                                        checked={active}
                                                        onChange={() => update({ modelSlug: item.slug }, 'model')}
                                                        className='sr-only'
                                                    />
                                                    <span className='min-w-0 flex-1'>
                                                        <span className='flex flex-wrap items-center gap-x-2 gap-y-1'>
                                                            <span className='font-heading font-expanded text-base font-bold text-white'>
                                                                <span data-mdlfy-cms={cmsTag('forklift_models', item.id, 'name')}>{item.name}</span>
                                                                {' '}
                                                                <span data-mdlfy-cms={cmsTag('forklift_models', item.id, 'edition')}>{item.edition}</span>
                                                            </span>
                                                            {item.badge && (
                                                                <span className='rounded-full bg-brand px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-foreground' data-mdlfy-cms={cmsTag('forklift_models', item.id, 'badge')}>
                                                                    {item.badge}
                                                                </span>
                                                            )}
                                                        </span>
                                                        <span className='mt-0.5 block text-sm text-white/60' data-mdlfy-cms={cmsTag('forklift_models', item.id, 'tagline')}>
                                                            {item.tagline}
                                                        </span>
                                                        <span className='mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-white/50'>
                                                            <span><span>{formatNumber(item.capacityKg)}</span> kg</span>
                                                            <span><span>{formatNumber(item.liftHeightM, 1)}</span> m lift</span>
                                                            <span><span>{formatNumber(item.batteryVoltage)}</span> V</span>
                                                        </span>
                                                    </span>
                                                    <span className='shrink-0 text-right'>
                                                        <span className='block text-[10px] uppercase tracking-wider text-white/45'>From</span>
                                                        <span className='text-sm font-semibold text-white'>{formatUsd(item.priceUsd)}</span>
                                                    </span>
                                                </label>
                                            )
                                        })}
                                    </div>
                                </fieldset>
                            )}

                            {tab === 'color' && (
                                <fieldset>
                                    <legend className='sr-only'>Choose a paint color</legend>
                                    <div className='grid grid-cols-3 gap-2.5'>
                                        {paints.map(paint => {
                                            const active = paint.key === config.paint

                                            return (
                                                <label key={paint.key} className={cn(cardClass(active), 'flex-col items-center gap-2 px-2 py-3.5 text-center')}>
                                                    <input
                                                        type='radio'
                                                        name={`${id}-paint`}
                                                        value={paint.key}
                                                        checked={active}
                                                        onChange={() => update({ paint: paint.key as PaintKey }, 'paint')}
                                                        className='sr-only'
                                                    />
                                                    <span
                                                        aria-hidden='true'
                                                        className='relative size-10 rounded-full ring-1 ring-white/20'
                                                        style={{ background: `radial-gradient(circle at 32% 28%, color-mix(in oklab, ${paint.hex} 55%, white) 0%, ${paint.hex} 45%, color-mix(in oklab, ${paint.hex} 65%, black) 100%)` }}
                                                    >
                                                        {active && (
                                                            <Check className='absolute inset-0 m-auto size-4 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]' />
                                                        )}
                                                    </span>
                                                    <span className='text-xs font-medium leading-tight text-white'>
                                                        {paint.name}
                                                    </span>
                                                    <span className='text-[11px] text-white/50'>
                                                        {priceLabel(paint.priceUsd)}
                                                    </span>
                                                </label>
                                            )
                                        })}
                                    </div>
                                </fieldset>
                            )}

                            {tab === 'power' && (
                                <div className='space-y-5'>
                                    <fieldset>
                                        <legend className='mb-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-white/50'>Battery</legend>
                                        <div className='grid grid-cols-2 gap-2.5'>
                                            {batteries.map(battery => {
                                                const active = battery.key === config.battery

                                                return (
                                                    <label key={battery.key} className={cn(cardClass(active), 'flex-col gap-1 p-4')}>
                                                        <input
                                                            type='radio'
                                                            name={`${id}-battery`}
                                                            value={battery.key}
                                                            checked={active}
                                                            onChange={() => update({ battery: battery.key as BatteryKey })}
                                                            className='sr-only'
                                                        />
                                                        <span className='text-sm font-semibold text-white'>{battery.name}</span>
                                                        <span className='text-xs text-brand'>
                                                            Up to <span>{formatNumber(runtimeFor(model, battery.key))}</span> h
                                                        </span>
                                                        <span className='mt-1 text-xs leading-snug text-white/55'>{battery.detail}</span>
                                                        <span className='mt-2 text-xs font-medium text-white/80'>{priceLabel(battery.priceUsd)}</span>
                                                    </label>
                                                )
                                            })}
                                        </div>
                                    </fieldset>

                                    <fieldset>
                                        <legend className='mb-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-white/50'>Tires</legend>
                                        <div className='grid grid-cols-2 gap-2.5'>
                                            {tires.map(tire => {
                                                const active = tire.key === config.tires

                                                return (
                                                    <label key={tire.key} className={cn(cardClass(active), 'flex-col gap-1 p-4')}>
                                                        <input
                                                            type='radio'
                                                            name={`${id}-tires`}
                                                            value={tire.key}
                                                            checked={active}
                                                            onChange={() => update({ tires: tire.key as TireKey }, 'tires')}
                                                            className='sr-only'
                                                        />
                                                        <span className='text-sm font-semibold text-white'>{tire.name}</span>
                                                        <span className='mt-1 text-xs leading-snug text-white/55'>{tire.detail}</span>
                                                        <span className='mt-2 text-xs font-medium text-white/80'>{priceLabel(tire.priceUsd)}</span>
                                                    </label>
                                                )
                                            })}
                                        </div>
                                    </fieldset>
                                </div>
                            )}

                            {tab === 'options' && (
                                <fieldset>
                                    <legend className='sr-only'>Choose options</legend>
                                    <div className='grid gap-2'>
                                        {options.map(option => {
                                            const active = config.options.includes(option.key)

                                            return (
                                                <label key={option.key} className={cn(cardClass(active), 'items-start gap-3 px-4 py-3.5')}>
                                                    <input
                                                        type='checkbox'
                                                        checked={active}
                                                        onChange={() => toggleOption(option.key)}
                                                        className='sr-only'
                                                    />
                                                    <span
                                                        aria-hidden='true'
                                                        className={cn(
                                                            'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md border transition-colors',
                                                            active ? 'border-brand bg-brand text-brand-foreground' : 'border-white/25'
                                                        )}
                                                    >
                                                        {active && <Check className='size-3.5' strokeWidth={3} />}
                                                    </span>
                                                    <span className='min-w-0 flex-1'>
                                                        <span className='block text-sm font-semibold text-white'>{option.name}</span>
                                                        <span className='mt-0.5 block text-xs leading-snug text-white/55'>{option.detail}</span>
                                                    </span>
                                                    <span className='shrink-0 text-xs font-medium text-white/80'>{priceLabel(option.priceUsd)}</span>
                                                </label>
                                            )
                                        })}
                                    </div>
                                </fieldset>
                            )}
                        </div>

                        <dl className='grid grid-cols-4 border-t border-white/10'>
                            {specs.map((spec, index) => (
                                <div key={spec.label} className={cn('flex flex-col-reverse gap-0.5 px-3 py-3 sm:px-4', index > 0 && 'border-l border-white/10')}>
                                    <dt className='text-[10px] uppercase tracking-wider text-white/45'>{spec.label}</dt>
                                    <dd className='text-sm font-semibold text-white'>
                                        <span>{spec.value}</span>
                                        <span className='ml-0.5 text-xs font-normal text-white/55'>{spec.unit}</span>
                                    </dd>
                                </div>
                            ))}
                        </dl>

                        <div className='flex flex-wrap items-end justify-between gap-4 border-t border-white/10 bg-white/[0.02] px-5 py-5 sm:px-6'>
                            <div>
                                <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-white/45'>Estimated price</p>
                                <p aria-hidden='true' className='mt-1 font-heading font-expanded text-2xl font-bold tabular-nums text-white'>
                                    {formatUsd(animatedTotal)}
                                </p>
                                <p className='sr-only' aria-live='polite'>
                                    {formatUsd(total)}
                                </p>
                                <p className='mt-0.5 text-xs text-white/55'>
                                    or about <span className='text-white/80'>{formatUsd(leaseFor(total))}</span> a month on a 60-month lease
                                </p>
                            </div>

                            <button
                                type='button'
                                onClick={request}
                                className='group inline-flex items-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-brand-foreground transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-[0_14px_40px_-12px_var(--brand)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand'
                            >
                                Request this build
                                <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
